// Touch gesture recognisers (chat gestures spec §3.1). Each is a small state machine fed with pointer events,
// so the thresholds are tested without a DOM; usePointerGestures wires them to elements.

/** The parts of a PointerEvent the recognisers read. */
export interface PointerSample {
  pointerId: number
  clientX: number
  clientY: number
  /** ms, as Event.timeStamp */
  timeStamp: number
}

/** A swipe starts after more than this much movement (px); a hold needs less than this. */
export const GESTURE_SLOP = 10
/** A swipe starts only when horizontal movement is at least this many times the vertical. */
export const HORIZONTAL_DOMINANCE = 1.5
export const HOLD_DELAY = 450
/** Swipe to reply: the message stops short of this, and letting go past the mark replies. */
export const REPLY_MAX = 72
export const REPLY_MARK = 56
/** Edge swipe back starts within this distance of the left edge; message swipes stay clear of it. */
export const EDGE_ZONE = 20
export const EDGE_GUARD = 24
/** px per ms: letting go this fast counts as a flick. */
export const FLICK_SPEED = 0.5
const FLICK_WINDOW = 100

export type Movement = 'pending' | 'horizontal' | 'vertical'

/** Whether movement from the start point is a horizontal gesture, a scroll, or not clear yet. */
export function classifyMovement(dx: number, dy: number): Movement {
  const across = Math.abs(dx)
  const down = Math.abs(dy)
  if (across > GESTURE_SLOP && across > HORIZONTAL_DOMINANCE * down) return 'horizontal'
  if (down > GESTURE_SLOP) return 'vertical'
  return 'pending'
}

/** Follows the distance for the first half of max, then stiffens towards max without reaching it. */
export function resist(distance: number, max: number): number {
  if (distance <= 0) return 0
  const free = max / 2
  if (distance <= free) return distance
  return free + (max - free) * (1 - Math.exp(-(distance - free) / (max - free)))
}

export interface Recogniser {
  down(event: PointerSample): void
  move(event: PointerSample): void
  up(event: PointerSample): void
  /** The browser took the pointer (a scroll) or the gesture must stop. */
  cancel(): void
}

export interface SwipeOptions {
  direction: 'left' | 'right'
  /** Offset (px) past which letting go commits; a function is read when the swipe starts. */
  mark: number | (() => number)
  /** With a limit the offset stiffens towards it (see resist); without one it follows the finger. */
  max?: number
  /** Only pointers that go down within these clientX bounds (inclusive) can start a swipe. */
  startX?: { min?: number; max?: number }
  /** Letting go at FLICK_SPEED or faster commits before the mark. */
  flick?: boolean
  onStart?: () => void
  onMove: (state: { offset: number; reached: boolean }) => void
  /** After onStart only: whether letting go commits. A cancelled swipe ends uncommitted. */
  onEnd: (result: { committed: boolean }) => void
}

/** A horizontal swipe in one direction, which vertical scrolling cancels (spec §3.1, §3.2, §3.4). */
export function createSwipe(options: SwipeOptions): Recogniser & { readonly active: boolean } {
  let start: PointerSample | null = null
  let active = false
  let mark = 0
  let samples: PointerSample[] = []

  const reset = () => {
    start = null
    active = false
    samples = []
  }
  const offsetAt = (event: PointerSample) => {
    const distance = (event.clientX - start!.clientX) * (options.direction === 'right' ? 1 : -1)
    return options.max === undefined ? Math.max(0, distance) : resist(distance, options.max)
  }
  const speedAt = (event: PointerSample) => {
    const recent = samples.filter((s) => event.timeStamp - s.timeStamp <= FLICK_WINDOW)
    const first = recent[0] ?? event
    const elapsed = event.timeStamp - first.timeStamp
    const distance = (event.clientX - first.clientX) * (options.direction === 'right' ? 1 : -1)
    return elapsed > 0 ? distance / elapsed : 0
  }

  return {
    get active() {
      return active
    },
    down(event) {
      if (start) {
        // A second finger: a pinch or a scroll, not a swipe.
        if (active) options.onEnd({ committed: false })
        reset()
        return
      }
      const { min, max } = options.startX ?? {}
      if ((min !== undefined && event.clientX < min) || (max !== undefined && event.clientX > max)) return
      start = event
      samples = [event]
    },
    move(event) {
      if (!start || event.pointerId !== start.pointerId) return
      samples.push(event)
      if (!active) {
        const dx = event.clientX - start.clientX
        const movement = classifyMovement(dx, event.clientY - start.clientY)
        if (movement === 'pending') return
        if (movement === 'vertical' || (dx > 0) !== (options.direction === 'right')) {
          reset()
          return
        }
        active = true
        mark = typeof options.mark === 'function' ? options.mark() : options.mark
        options.onStart?.()
      }
      const offset = offsetAt(event)
      options.onMove({ offset, reached: offset >= mark })
    },
    up(event) {
      if (!start || event.pointerId !== start.pointerId) return
      if (active) {
        const committed = offsetAt(event) >= mark || (Boolean(options.flick) && speedAt(event) >= FLICK_SPEED)
        options.onEnd({ committed })
      }
      reset()
    },
    cancel() {
      if (active) options.onEnd({ committed: false })
      reset()
    },
  }
}

export interface HoldOptions {
  delay?: number
  /** The finger must drift less than this (px) while holding. */
  tolerance?: number
  onHold: (start: PointerSample) => void
}

/** Press and hold (spec §3.1): fires after the delay unless the finger moves, lifts or a second one lands. */
export function createHold(options: HoldOptions): Recogniser & {
  readonly held: boolean
  /** Fires now if still holding, e.g. when the system's own long press comes first. Returns whether it fired. */
  fire(): boolean
} {
  const delay = options.delay ?? HOLD_DELAY
  const tolerance = options.tolerance ?? GESTURE_SLOP
  let start: PointerSample | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  let held = false

  const stop = () => {
    clearTimeout(timer)
    timer = undefined
    start = null
  }
  const fire = () => {
    if (!start) return false
    const from = start
    stop()
    held = true
    options.onHold(from)
    return true
  }

  return {
    get held() {
      return held
    },
    fire,
    down(event) {
      if (start) {
        stop()
        return
      }
      held = false
      start = event
      timer = setTimeout(fire, delay)
    },
    move(event) {
      if (!start || event.pointerId !== start.pointerId) return
      if (Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY) >= tolerance) stop()
    },
    up(event) {
      if (start && event.pointerId === start.pointerId) stop()
    },
    cancel: stop,
  }
}

export interface PinchState {
  scale: number
  centerX: number
  centerY: number
}

export interface PinchOptions {
  onStart?: (state: PinchState) => void
  onChange: (state: PinchState) => void
  onEnd?: (state: PinchState) => void
}

/** Two-finger pinch: scale relative to the starting distance, around the point between the fingers. */
export function createPinch(options: PinchOptions): Recogniser & { readonly active: boolean } {
  const points = new Map<number, PointerSample>()
  let startDistance = 0
  let last: PinchState | null = null

  const measure = (): { distance: number; centerX: number; centerY: number } => {
    const [a, b] = [...points.values()]
    return {
      distance: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
      centerX: (a.clientX + b.clientX) / 2,
      centerY: (a.clientY + b.clientY) / 2,
    }
  }
  const end = () => {
    if (last) options.onEnd?.(last)
    last = null
  }

  return {
    get active() {
      return last !== null
    },
    down(event) {
      if (points.size >= 2) return
      points.set(event.pointerId, event)
      if (points.size < 2) return
      const { distance, centerX, centerY } = measure()
      startDistance = distance || 1
      last = { scale: 1, centerX, centerY }
      options.onStart?.(last)
    },
    move(event) {
      if (!points.has(event.pointerId)) return
      points.set(event.pointerId, event)
      if (!last) return
      const { distance, centerX, centerY } = measure()
      last = { scale: distance / startDistance, centerX, centerY }
      options.onChange(last)
    },
    up(event) {
      if (!points.delete(event.pointerId)) return
      end()
    },
    cancel() {
      points.clear()
      end()
    },
  }
}
